import { SITE_SERVER_URL } from "@/shared/config/player";
import { referralBody } from "@/shared/lib/referral";
import { getAttributionPayload } from "@/shared/lib/analytics";

/**
 * Пакеты «умный поиск + вопросы специалисту» (.com, «Версия 2 — для
 * утверждения · 09.10.2026»): кошелёк пишет только site_server
 * (routers_fastapi/sup_plan.py). Здесь — чтение вида, оплата пакета и
 * «уведомление показано». Сеанс — подписанный site_session_token, как у
 * /api/learning/sup-spend.
 */

export type PlanMode = "plans" | "tokens" | "guest";

export interface PlanOffer {
    months: number;
    questions: number;
    /** Цена для этого человека (подписчику — уже со скидкой), центы. */
    cents: number;
    regularCents: number;
    offPct: number;
    priceKind?: "regular" | "subscriber";
    productId?: string;
}

/** buyer_month — месяц поиска в подарок за покупку на сайте или подписку приложения (11.10). */
export type PlanNoticeId = "migration" | "trial" | "app_year" | "buyer_month";

export interface PlanNotice {
    id: PlanNoticeId;
    /** migration: сколько было токенов и сколько стало вопросов. */
    tokens?: number;
    questions?: number;
    until?: string | null;
}

export interface PlanReminder {
    kind: "d7" | "d1";
    left: number;
    until: string;
}

export interface PlanWallet {
    mode: PlanMode;
    subscriber: boolean;
    offers: PlanOffer[];
    currency: string;
    /**
     * Поиск: открыт ли, до какой даты, чем открыт; `paid` — открыт ОПЛАЧЕННЫМ сроком
     * (сервер: sup_plan.paid_open) — только тогда новый поиск даёт +50 «Обучения».
     */
    search?: { open: boolean; until: string | null; source?: string | null; paid?: boolean };
    questions?: { left: number; until: string | null; active: boolean };
    reminder?: PlanReminder | null;
    notices?: PlanNotice[];
    viewed?: string[];
    dailyCapped?: boolean;
    seq?: number;
    /** Гостю (mode=guest): новый аккаунт получит 7 дней поиска в подарок (выключатель «all»). */
    trialForNew?: boolean;
    /**
     * Месяц поиска в подарок за покупку (grants["buyer"]): `pending` — ждёт первого захода в
     * умный поиск (начнётся сам: GET /wallet с visit=1 или любая трата поиска); null — подарка нет.
     */
    buyerMonth?: { pending: boolean; activatedAt: string | null } | null;
    /**
     * Лекции и клипы этому человеку — под воротами умного поиска (сервер: sup_plan.feature_public;
     * владелец 12.10: пока выключатель не «all» — только админы и тестеры). Во всех видах (гость,
     * токены, пакеты). false или нет поля (прежний сервер) — как до 11.10: лекции и клипы
     * бесплатно, месяца в подарок не видно.
     */
    lecturesGate?: boolean;
}

/** Пакеты по умолчанию — те же, что на сервере (sup_plan.PACKS); пока вид не пришёл. */
export const DEFAULT_OFFERS: PlanOffer[] = [
    { months: 1, questions: 3, cents: 990, regularCents: 990, offPct: 0 },
    { months: 3, questions: 9, cents: 2690, regularCents: 2690, offPct: 0 },
    { months: 6, questions: 18, cents: 4990, regularCents: 4990, offPct: 0 },
    { months: 12, questions: 36, cents: 8990, regularCents: 8990, offPct: 0 },
];

/** Подписанный сеанс сайта — единственный, который читает сервер пакетов. */
export const planSessionToken = (): string => {
    try {
        return (localStorage.getItem("site_session_token") || "").trim();
    } catch {
        return "";
    }
};

const num = (v: unknown, d = 0): number => {
    const n = Number(v);
    return Number.isFinite(n) ? n : d;
};

const str = (v: unknown): string | null => (typeof v === "string" && v ? v : null);

const NOTICE_IDS: PlanNoticeId[] = ["migration", "trial", "app_year", "buyer_month"];

/** Вид кошелька из ответа сервера (GET /wallet, тело траты, 402/429) — в одной форме. */
export const normalizePlanWallet = (raw: unknown): PlanWallet | null => {
    const d = raw as Record<string, unknown> | null;
    if (!d || typeof d !== "object") return null;
    const mode = d.mode === "plans" || d.mode === "tokens" || d.mode === "guest" ? d.mode : null;
    if (!mode) return null;
    const offers = Array.isArray(d.offers)
        ? (d.offers as Record<string, unknown>[])
              .map((o) => ({
                  months: num(o.months),
                  questions: num(o.questions),
                  cents: num(o.cents),
                  regularCents: num(o.regularCents, num(o.cents)),
                  offPct: num(o.offPct),
                  priceKind: o.priceKind === "subscriber" ? ("subscriber" as const) : ("regular" as const),
                  productId: str(o.productId) || undefined,
              }))
              .filter((o) => o.months > 0 && o.cents > 0)
        : [];
    // Новое 11.10 (лекции в СУПе, месяц в подарок) — только тем, кому сервер его уже показывает.
    const gate = d.lecturesGate === true;
    const w: PlanWallet = {
        mode,
        subscriber: d.subscriber === true,
        offers: offers.length ? offers : DEFAULT_OFFERS,
        currency: str(d.currency) || "usd",
        lecturesGate: gate,
    };
    if (mode === "guest") w.trialForNew = d.trialForNew === true;
    if (mode !== "plans") return w;
    const s = (d.search || {}) as Record<string, unknown>;
    const q = (d.questions || {}) as Record<string, unknown>;
    const r = d.reminder as Record<string, unknown> | null | undefined;
    w.search = { open: s.open === true, until: str(s.until), source: str(s.source), paid: s.paid === true };
    w.questions = { left: Math.max(0, num(q.left)), until: str(q.until), active: q.active === true };
    w.reminder =
        r && (r.kind === "d7" || r.kind === "d1") && str(r.until)
            ? { kind: r.kind, left: Math.max(0, num(r.left)), until: str(r.until) as string }
            : null;
    w.notices = Array.isArray(d.notices)
        ? (d.notices as Record<string, unknown>[])
              .filter((n) => NOTICE_IDS.includes(n.id as PlanNoticeId))
              // Месяц в подарок — только при воротах (сервер и сам не шлёт; прежний сервер — шлёт).
              .filter((n) => gate || n.id !== "buyer_month")
              .map((n) => ({
                  id: n.id as PlanNoticeId,
                  tokens: n.tokens === undefined ? undefined : num(n.tokens),
                  questions: n.questions === undefined ? undefined : num(n.questions),
                  until: str(n.until),
              }))
        : [];
    w.viewed = Array.isArray(d.viewed) ? (d.viewed as unknown[]).filter((v): v is string => typeof v === "string") : [];
    w.dailyCapped = d.dailyCapped === true;
    const bm = gate ? (d.buyerMonth as Record<string, unknown> | null | undefined) : null;
    w.buyerMonth = bm && typeof bm === "object" ? { pending: bm.pending === true, activatedAt: str(bm.activatedAt) } : null;
    w.seq = num(d.seq);
    return w;
};

export class PlanApiError extends Error {
    readonly status: number;
    constructor(code: string, status: number) {
        super(code);
        this.name = "PlanApiError";
        this.status = status;
    }
}

/**
 * Сервер не смог сказать, в каком режиме человек (503 wallet_unavailable — хранилище не
 * ответило; 409 wallet_busy — кошелёк занят): это НЕ «токены». Страница показывает
 * «не удалось загрузить» и переспрашивает, а не прежний экран токенов.
 * Только по коду в ответе: любой другой 503/409 (сбой прокси, перезапуск) — не повод
 * прятать токены у тех, кто на токенах.
 */
const MODE_UNKNOWN_CODES = new Set(["wallet_unavailable", "wallet_busy"]);
export const isModeUnknownError = (e: unknown): boolean =>
    e instanceof PlanApiError && (e.status === 503 || e.status === 409) && MODE_UNKNOWN_CODES.has(e.message);

/** Часовой пояс браузера: «местные сутки» скрытого предела и даты напоминаний. */
export const browserTimeZone = (): string => {
    try {
        return Intl.DateTimeFormat().resolvedOptions().timeZone || "";
    } catch {
        return "";
    }
};

const WALLET_TIMEOUT_MS = 8000;

/** Адрес сервера пакетов; сеанс — в заголовке (Authorization: Bearer), не в адресе: адреса оседают в журналах прокси. */
const planUrl = (path: string, params: Record<string, string | undefined> = {}): string => {
    const q = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => {
        if (v) q.set(k, v);
    });
    const qs = q.toString();
    return `${SITE_SERVER_URL}${path}${qs ? `?${qs}` : ""}`;
};

/** Заголовки запроса: подписанный сеанс сайта — Bearer (его же принимает site_server у остальных). */
const planHeaders = (json = false): Record<string, string> => {
    const h: Record<string, string> = {};
    const token = planSessionToken();
    if (token) h.Authorization = `Bearer ${token}`;
    if (json) h["Content-Type"] = "application/json";
    return h;
};

const errorOf = async (res: Response): Promise<PlanApiError> => {
    const body = (await res.json().catch(() => null)) as { error?: string; detail?: string } | null;
    return new PlanApiError(body?.error || body?.detail || `http_${res.status}`, res.status);
};

/**
 * GET /api/sup-plan/wallet. `fresh` — подписку проверить мимо кэша (окно пакетов, возврат с
 * оплаты). Без сеанса — вид гостя (цены и «что даст вход»).
 * `visit` — это заход в умный поиск (СУП, «Мои лекции»): ждущий месяц в подарок за покупку
 * начинается. Страницам, которым нужен только баланс (меню, витрины, чат специалиста), —
 * visit=0: подарок ждёт настоящего захода. Параметр шлём всегда: сервер без него заходом НЕ
 * считает (visit=0 по умолчанию — прежние сборки сайта месяц не начинают).
 */
export async function fetchPlanWallet(
    opts: { fresh?: boolean; lang?: string; visit?: boolean } = {},
): Promise<PlanWallet> {
    const ctrl = typeof AbortController !== "undefined" ? new AbortController() : null;
    const timer = ctrl ? setTimeout(() => ctrl.abort(), WALLET_TIMEOUT_MS) : null;
    try {
        const res = await fetch(
            planUrl("/api/sup-plan/wallet", {
                lang: opts.lang,
                tz: browserTimeZone() || undefined,
                fresh: opts.fresh ? "1" : undefined,
                visit: opts.visit ? "1" : "0",
            }),
            { method: "GET", headers: planHeaders(), signal: ctrl?.signal },
        );
        if (!res.ok) throw await errorOf(res);
        const body = (await res.json()) as { data?: unknown };
        const w = normalizePlanWallet(body?.data);
        if (!w) throw new PlanApiError("bad_wallet", 200);
        return w;
    } finally {
        if (timer) clearTimeout(timer);
    }
}

export interface PlanCheckout {
    url: string;
    months: number;
    questions: number;
    cents: number;
    regularCents: number;
    priceKind: string;
}

/** POST /api/sup-plan/checkout: цену выбирает сервер (годовая подписка — вживую). */
export async function startPlanCheckout(
    months: number,
    opts: { returnPath?: string | null; lang?: string } = {},
): Promise<PlanCheckout> {
    const attribution = getAttributionPayload();
    const res = await fetch(planUrl("/api/sup-plan/checkout"), {
        method: "POST",
        headers: planHeaders(true),
        body: JSON.stringify({
            months,
            returnPath: opts.returnPath || undefined,
            lang: opts.lang,
            ...referralBody(),
            ...(attribution ? { attribution } : {}),
        }),
    });
    if (!res.ok) throw await errorOf(res);
    const body = (await res.json()) as { data?: Record<string, unknown> };
    const d = body?.data || {};
    const url = str(d.url);
    if (!url) throw new PlanApiError("no_url", res.status);
    return {
        url,
        months: num(d.months, months),
        questions: num(d.questions),
        cents: num(d.cents),
        regularCents: num(d.regularCents),
        priceKind: str(d.priceKind) || "regular",
    };
}

/** POST /api/sup-plan/notices/{id}/seen — уведомление показано, больше не придёт. */
export async function markNoticeSeen(id: PlanNoticeId): Promise<boolean> {
    try {
        const res = await fetch(planUrl(`/api/sup-plan/notices/${encodeURIComponent(id)}/seen`), {
            method: "POST",
            headers: planHeaders(),
        });
        return res.ok;
    } catch {
        return false;
    }
}
